import React from 'react';

import { SideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { TopNav } from '@atlaskit/navigation-system/layout/top-nav';
import { GSNSkeleton } from '@atlassian/global-side-navigation/skeleton';
import { GTNSkeleton } from '@atlassian/global-top-navigation/skeleton';

/**
 * Props for {@link AppChromeSkeleton}.
 */
interface AppChromeSkeletonProps {
	/**
	 * Whether to render a side nav skeleton.
	 *
	 * Should mirror whether the app renders a side nav at all: apps that opt out
	 * by passing `SideNavSlot={null}` must not be given a side nav skeleton, or
	 * the layout would shift as the placeholder disappears.
	 */
	hasSideNav: boolean;

	/**
	 * Whether to render a top nav skeleton.
	 *
	 * Should mirror whether the app renders a top nav at all: apps that opt out
	 * by passing `TopNavSlot={null}` must not be given a top nav skeleton, or
	 * the layout would shift as the placeholder disappears.
	 */
	hasTopNav: boolean;
}

/**
 * The loading state rendered while `AppChrome` waits for the app chrome query.
 *
 * It reserves the same layout slots the real chrome occupies — `TopNav`, and
 * `SideNav` when the app has one — so the page does not reflow when the data
 * arrives. The contents of each come from that navigation's own skeleton —
 * GTN's `GTNSkeleton` and GSN's `GSNSkeleton` — so the placeholder stays in
 * step with the real navigation it stands in for.
 */
export function AppChromeSkeleton({
	hasSideNav,
	hasTopNav,
}: AppChromeSkeletonProps): React.JSX.Element {
	return (
		<>
			{hasTopNav && (
				<TopNav testId="global-root-topnav-skeleton">
					<GTNSkeleton />
				</TopNav>
			)}
			{hasSideNav && (
				<SideNav testId="global-root-sidenav-skeleton">
					<GSNSkeleton />
				</SideNav>
			)}
		</>
	);
}
